import { useState, useEffect, useReducer, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

import { parseResponse, parseError } from './parseResponse';

const fetchReducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_INIT':
            return {
                ...state,
                isLoading: true,
                isError: false
            };
        case 'FETCH_SUCCESS':
            return {
                ...state,
                isLoading: false,
                isError: false,
                response: action.payload,
            };
        case 'FETCH_FAILURE':
            return {
                ...state,
                isLoading: false,
                isError: true,
                response: action.payload
            };
        case 'RESET':
            return {
                isLoading: false,
                isError: false,
                response: null,
            }
        default:
            throw new Error();
    }
}

/** Stable key so array/object payloads do not retrigger fetches on new references. */
function serializePayloadKey(payload) {
    if (payload === undefined) {
        return '';
    }
    if (typeof payload !== 'object' || payload === null) {
        return String(payload);
    }
    try {
        return JSON.stringify(payload);
    } catch {
        return String(payload);
    }
}

/**
 * Executes an API call and returns the result. Tracks isLoading and
 * isError flags.
 * 
 * Can either be passed a funcition that returns a promise or a config 
 * object.  If a function is passed, that will be used to execute the API 
 * Call.  If no function is passed the config object should be the first 
 * agrument and will be used to execute a call with axios.
 * 
 * @param {() => Promise || Object} fn Function to call or config object
 * @param {Object} config Config used when a function is passed
 */
export const useAPICall = (fn, config = {}) => {

    // Allow function to be passed as first argument or only a config
    // object to be passed.
    if (typeof fn !== 'function') {
        config = fn;
        fn = null;
    }

    const { 
        url: defaultURL, 
        payload: defaultPayload, 
        method = 'get', 
        onLoad = true } = config;

    const [{ url, payload, ready }, setConfig] = useState({ url: defaultURL, payload: defaultPayload, method, ready: onLoad });
    const [state, dispatch] = useReducer(fetchReducer, {
        isLoading: false,
        isError: false,
        response: null
    })

    const setAPICall = useCallback((newState) => {
        setConfig((state) => ({ ...state, ...newState, ready: true }))
    }, []);

    const resetAPICall = useCallback(() => {
        setConfig({ url: defaultURL, payload: defaultPayload, method, ready: onLoad });
        dispatch({ type: 'RESET' });
    }, [defaultURL, defaultPayload, method, onLoad]);

    const payloadKey = useMemo(() => serializePayloadKey(payload), [payload]);
    const payloadRef = useRef(payload);
    payloadRef.current = payload;

    useEffect(() => {
        const fetchData = () => {
            const currentPayload = payloadRef.current;

            dispatch({ type: 'FETCH_INIT' });
            const Func = typeof fn === 'function'
                ? () => {
                    return Array.isArray(currentPayload) 
                        ? fn(...currentPayload) 
                        : fn(currentPayload)
                }
                : () => axios({ method, url, data: currentPayload })

            Func()
                .then((result) => {
                    const response = parseResponse(result);
                    if (response && response.status >= 400) {
                        throw response;
                    }
                    dispatch({ type: 'FETCH_SUCCESS', payload: response })
                })
                .catch((error) => {
                    const response = parseError(error);
                    dispatch({ type: 'FETCH_FAILURE', payload: response })
                })
        }

        if (ready) {
            fetchData();
        }

    }, [ready, url, payloadKey, fn, method])

    return [state, setAPICall, resetAPICall]

}
